import {combineLatest as observableCombineLatest, Observable} from 'rxjs';

import {takeWhile, first, catchError} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from '@angular/core';
import {ResourceService} from '../../resource.service';
import {Router} from '@angular/router';
import {ViewTabComponent} from '../viewtab.component';
import {AbstractControlReadonlyComponent} from './abstract-control-readonly';
import {ErrorService} from '../../../error/error.service';
import {ActionBarConfiguration} from '../../../cache/uiconfiguration/actionbarconfiguration';
import {CacheService} from '../../../cache/cache.service';
import {AddViewEvent} from '../../events/addviewevent';
import {DialogConfiguration} from '../../../cache/uiconfiguration/dialogconfiguration';

@Component({
  selector: 'ctrl-actionbar',
  template: `
    @for (item of actionBarItemsLeft; track item) {
      <span>
        @if (item.theme === 'raised') {
          <button
            mat-raised-button
            [color]="item.type"
            (click)="_onActionClick(item)"
            matTooltip="{{ item.description }}"
            [disabled]="isActionBarItemDisabled(item)"
          >
            <span>{{ item.displayname }}</span>
          </button>
        }
        @if (item.theme === 'flat') {
          <button
            mat-button
            [color]="item.type"
            (click)="_onActionClick(item)"
            matTooltip="{{ item.description }}"
            [disabled]="isActionBarItemDisabled(item)"
          >
            <span>{{ item.displayname }}</span>
          </button>
        }
        @if (item.theme === 'icon') {
          <button
            mat-icon-button
            [color]="item.type"
            (click)="_onActionClick(item)"
            matTooltip="{{ item.description }}"
            [disabled]="isActionBarItemDisabled(item)"
          >
            <mat-icon>{{ item.icon }}</mat-icon>
          </button>
        }
      </span>
    }
    <div class="actionbar-spacer"></div>
    @for (item of actionBarItemsRight; track item) {
      <span>
        @if (item.theme === 'raised') {
          <button
            mat-raised-button
            [color]="item.type"
            (click)="_onActionClick(item)"
            matTooltip="{{ item.description }}"
            [disabled]="isActionBarItemDisabled(item)"
          >
            <span>{{ item.displayname }}</span>
          </button>
        }
        @if (item.theme === 'flat') {
          <button
            mat-button
            [color]="item.type"
            (click)="_onActionClick(item)"
            matTooltip="{{ item.description }}"
            [disabled]="isActionBarItemDisabled(item)"
          >
            <span>{{ item.displayname }}</span>
          </button>
        }
        @if (item.theme === 'icon') {
          <button
            mat-icon-button
            [color]="item.type"
            (click)="_onActionClick(item)"
            matTooltip="{{ item.description }}"
            [disabled]="isActionBarItemDisabled(item)"
          >
            <mat-icon>{{ item.icon }}</mat-icon>
          </button>
        }
      </span>
    }
  `,
  styleUrls: ['./ctrl-actionbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlActionBarComponent extends AbstractControlReadonlyComponent {
  constructor(
    private router: Router,
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    errorService: ErrorService,
    private cacheService: CacheService,
  ) {
    super(parent, 'LinkedObject', changeDetector, errorService);
  }

  actionBarItems: ActionBarConfiguration[] = null;
  linkedObject: string = null;

  protected initMain(): void {
    if (this._config.propertyExists('ActionBarResources')) {
      this._config
        .getPropertyValues('ActionBarResources')
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((keywordsStr: any) => {
          if (keywordsStr == null || !(typeof keywordsStr === 'string')) return;
          const keywords: string[] = keywordsStr.split(',').map(x => x.trim());
          if (keywords == null || keywords.length === 0) return;

          const listAbcObs: Observable<ActionBarConfiguration[]>[] = [];
          keywords.forEach((keyword: string) => {
            listAbcObs.push(this.cacheService.getActionBarConfigurationsForObjectType(keyword));
          });

          observableCombineLatest(listAbcObs)
            .pipe(
              catchError((error, caught) => this.handleError(error, caught)),
              first(),
            )
            .subscribe((abcs: ActionBarConfiguration[][]) => {
              this.actionBarItems = [].concat.apply([], abcs);
              this.changeDetector.markForCheck();
            });
        });
    }

    this.sourceObs.pipe(takeWhile(() => this.alive)).subscribe(value => {
      this.linkedObject = value;
      this.changeDetector.markForCheck();
    });

    this.changeDetector.markForCheck();
  }

  get actionBarItemsLeft(): ActionBarConfiguration[] {
    if (this.actionBarItems == null) return [];
    return this.actionBarItems
      .filter(value => value.position != 'right')
      .sort((a, b) => a.order - b.order);
  }

  get actionBarItemsRight(): ActionBarConfiguration[] {
    if (this.actionBarItems == null) return [];
    return this.actionBarItems
      .filter(value => value.position == 'right')
      .sort((a, b) => a.order - b.order);
  }

  isActionBarItemDisabled(conf: ActionBarConfiguration): boolean {
    if (['wizard', 'batch', 'batchlist'].indexOf(conf.sitetype) > -1) {
      return this.linkedObject == null;
    } else {
      return false;
    }
  }

  _onActionClick(conf: ActionBarConfiguration) {
    if (conf.sitetype === 'objectlist') conf.sitetype = 'olist';
    if (['get', 'timeline'].indexOf(conf.sitetype) > -1) {
      if (conf.urlid) {
        this._parent.onAddView.emit(new AddViewEvent(conf.sitetype, conf.urlid));
      } else {
        if (conf.linkedobject == null) {
          if (this.linkedObject == null || !ResourceService.guidRegex.test(this.linkedObject))
            return;
          this._parent.onAddView.emit(new AddViewEvent(conf.sitetype, this.linkedObject));
        } else {
          this._parent.onAddView.emit(new AddViewEvent(conf.sitetype, conf.linkedobject));
        }
      }
    } else if (['list', 'olist'].indexOf(conf.sitetype) > -1) {
      this._parent.onAddView.emit(new AddViewEvent(conf.sitetype, conf.urlid));
    } else if (conf.sitetype === 'create') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(new AddViewEvent('create', dgConf.urlid));
        });
    } else if (conf.sitetype === 'wizard') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(new AddViewEvent('wizard', dgConf.urlid, this.linkedObject));
        });
    } else if (conf.sitetype === 'batch') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(new AddViewEvent('batch', dgConf.urlid, this.linkedObject));
        });
    } else if (conf.sitetype === 'batchlist') {
      this.cacheService
        .getDialogConfigurationByID(conf.linkedobject)
        .pipe(
          catchError((error, caught) => this.handleError(error, caught)),
          first(),
        )
        .subscribe((dgConf: DialogConfiguration) => {
          this._parent.onAddView.emit(
            new AddViewEvent('batchlist', dgConf.urlid, this.linkedObject),
          );
        });
    } else if (conf.sitetype === 'external') {
      this._parent.onAddView.emit(new AddViewEvent('external', conf.urlid));
    } else {
      let command: any[] = [''];
      if (conf.sitetype != null) command.push(conf.sitetype);
      if (conf.urlid != null) command.push(conf.urlid);
      if (conf.linkedobject != null) command.push(conf.linkedobject);
      this.router.navigate(command);
    }
  }
}
